/**
 * Find People Cloud Function service.
 * Wraps the three discovery Cloud Functions: by interest, by location, and currently online.
 * All functions require authentication — the Cloud Functions validate auth and use
 * the caller's profile data (interests, city, timezone) for matching.
 */

import { httpsCallable } from 'firebase/functions';
import { FIREBASE_FUNCTIONS } from '../firebaseConfig';
import type { FindPplResponse } from '../types/pplTalk';

// ─── Callable references ────────────────────────────────────────────────────

const getPplByInterestFn = httpsCallable<
  { userId: string; limit?: number; lastDocId?: string; getTotalCount?: boolean },
  FindPplResponse
>(FIREBASE_FUNCTIONS, 'getPplBasedInterest');

const getPplByLocationFn = httpsCallable<
  { userId: string; limit?: number; lastDocId?: string; getTotalCount?: boolean },
  FindPplResponse
>(FIREBASE_FUNCTIONS, 'getPplBasedLocation');

const getPplOnlineFn = httpsCallable<
  { userId: string; limit?: number; lastDocId?: string; getTotalCount?: boolean },
  FindPplResponse
>(FIREBASE_FUNCTIONS, 'getPplCurrentlyOnline');

interface InteractedUser {
  id: string;
  tetherHandle: string | null;
  profileImageUrl: string | null;
  aboutMe: string | null;
  city: string | null;
  country: string | null;
  lastInteractionAt: number;
  sources: Array<'chat' | 'like' | 'reply'>;
}

const getInteractedPplFn = httpsCallable<
  { limit?: number },
  { success: boolean; users: InteractedUser[] }
>(FIREBASE_FUNCTIONS, 'getInteractedPplEurW');

// ─── Exported service functions ─────────────────────────────────────────────

export type FindPplTab = 'location' | 'interests' | 'online' | 'interacted';

export interface FindPplParams {
  userId: string;
  limit?: number;
  lastDocId?: string;
  getTotalCount?: boolean;
}

export async function getPplByInterest(params: FindPplParams): Promise<FindPplResponse> {
  const result = await getPplByInterestFn({
    userId: params.userId,
    limit: params.limit ?? 20,
    lastDocId: params.lastDocId,
    getTotalCount: params.getTotalCount,
  });
  return result.data;
}

export async function getPplByLocation(params: FindPplParams): Promise<FindPplResponse> {
  const result = await getPplByLocationFn({
    userId: params.userId,
    limit: params.limit ?? 20,
    lastDocId: params.lastDocId,
    getTotalCount: params.getTotalCount,
  });
  return result.data;
}

export async function getPplOnline(params: FindPplParams): Promise<FindPplResponse> {
  const result = await getPplOnlineFn({
    userId: params.userId,
    limit: params.limit ?? 20,
    lastDocId: params.lastDocId,
    getTotalCount: params.getTotalCount,
  });
  return result.data;
}

export async function getInteractedPpl(limit = 20): Promise<FindPplResponse> {
  const result = await getInteractedPplFn({ limit });
  const raw = result.data?.users || [];
  return {
    users: raw.map((u) => ({
      id: u.id,
      uid: u.id,
      handle: u.tetherHandle || undefined,
      avatar: u.profileImageUrl || undefined,
      aboutMe: u.aboutMe || undefined,
      city: u.city || undefined,
      country: u.country || undefined,
      profileSet: true,
      matchType: u.sources.join(','),
    })),
    hasMore: false,
    totalCount: raw.length,
  };
}
